/**
 * Toolbar for chart actions: type switching, export, and reset.
 */

import { BarChart2, LineChart, PieChart, AreaChart, ScatterChart } from 'lucide-react';
import { Download, LayoutDashboard, RotateCcw, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/ui/toggle-group';
import type { ChartType, ChartExportFormat } from '../types/chartTypes';
import { CHART_TYPE_LABELS } from '../types/chartTypes';

type ChartToolbarProps = {
  readonly currentType: ChartType;
  readonly onTypeChange: (type: ChartType) => void;
  readonly onExport: (format: ChartExportFormat) => void;
  readonly onReset: () => void;
  readonly onConfigure: () => void;
  readonly isExporting?: boolean;
  /** S20 — pin this chart to a dashboard (present only on the QE surface). */
  readonly onPinToDashboard?: () => void;
};

const CHART_TYPE_ICONS: Record<ChartType, typeof BarChart2> = {
  bar: BarChart2,
  line: LineChart,
  pie: PieChart,
  area: AreaChart,
  scatter: ScatterChart,
};

export function ChartToolbar({
  currentType,
  onTypeChange,
  onExport,
  onReset,
  onConfigure,
  isExporting = false,
  onPinToDashboard,
}: ChartToolbarProps) {
  return (
    <div className="flex items-center justify-between px-2 py-1 border-b bg-muted/30 select-none">
      <div className="flex items-center gap-2">
        {/* Chart Type Switcher */}
        <ToggleGroup
          type="single"
          value={currentType}
          onValueChange={(value: string) => {
            if (value) onTypeChange(value as ChartType);
          }}
          className="bg-background rounded-md border"
        >
          {(['bar', 'line', 'pie', 'area', 'scatter'] as const).map((type) => {
            const Icon = CHART_TYPE_ICONS[type];
            return (
              <ToggleGroupItem
                key={type}
                value={type}
                aria-label={CHART_TYPE_LABELS[type]}
                className="h-7 w-7 p-0"
                title={CHART_TYPE_LABELS[type]}
                data-testid={`chart-type-${type}`}
              >
                <Icon className="h-3.5 w-3.5" />
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>

        {/* Configure Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onConfigure}
          className="h-7 px-2 gap-1"
          title="Configure chart"
          data-testid="chart-configure-btn"
        >
          <Settings2 className="h-3.5 w-3.5" />
          <span className="text-xs">Configure</span>
        </Button>
      </div>

      <div className="flex items-center gap-1">
        {/* Pin to dashboard (S20) */}
        {onPinToDashboard && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onPinToDashboard}
            className="h-7 px-2 gap-1"
            title="Pin to dashboard"
            data-testid="chart-pin-dashboard-btn"
          >
            <LayoutDashboard className="h-3.5 w-3.5" />
            <span className="text-xs">Pin</span>
          </Button>
        )}

        {/* Export Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              disabled={isExporting}
              className="h-7 px-2 gap-1"
              title="Export chart"
              data-testid="chart-export-btn"
            >
              <Download className="h-3.5 w-3.5" />
              <span className="text-xs">Export</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => onExport('png')}
              data-testid="export-png"
            >
              Export as PNG
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onExport('svg')}
              data-testid="export-svg"
            >
              Export as SVG
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Reset Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          className="h-7 px-2 gap-1"
          title="Reset chart"
          data-testid="chart-reset-btn"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          <span className="text-xs">Reset</span>
        </Button>
      </div>
    </div>
  );
}
